/**
 * Copyright 2026 Bluecircle Technologies Pvt Limited. All rights reserved.
 *
 * This software and its source code are the proprietary and confidential
 * intellectual property of Bluecircle Technologies Pvt Limited. Unauthorized
 * copying, reproduction, distribution, modification, public display, or use
 * of this software, in whole or in part, is strictly prohibited without the
 * prior written permission of Bluecircle Technologies Pvt Limited.
 */

/**
 * The workspace page's setup banner (openspec add-account-workspace-setup 4.5, persist-setup-status):
 * while the workspace's services are being set up, when one failed, or when setup must run again
 * or never ran, it lists every service with its state and offers Retry to the owner. A ready
 * workspace shows its owner only a "Re-check setup" link; members see nothing (nor when KNet setup
 * is off).
 *
 * KnetOrgSetupNotice is the org home page's pointer to the org's workspaces that need setup.
 */

import { makeT } from "app/client/lib/localization";
import { reportError } from "app/client/models/errors";
import { urlState } from "app/client/models/gristUrlState";
import { textButton } from "app/client/ui2018/buttons";
import { theme } from "app/client/ui2018/cssVars";
import {
  finishedSteps, setupStepLabel, setupStepRows, showSetupBanner, totalSteps, WorkspaceSetupView,
} from "app/common/knetSetup";
import { fetchFromHome } from "app/common/urlUtils";

import { Disposable, dom, makeTestId, Observable, styled } from "grainjs";

const t = makeT("KnetSetupBanner");
const testId = makeTestId("test-knet-setup-");

const POLL_MS = 5000;
const MARKS: Record<string, string> = { ready: "✓", skipped: "–", failed: "✗", pending: "·" };

export class KnetSetupBanner extends Disposable {
  private _view = Observable.create<WorkspaceSetupView | null>(this, null);
  private _retrying = Observable.create(this, false);
  private _timer?: ReturnType<typeof setTimeout>;

  constructor(private _wsId: number) {
    super();
    this.onDispose(() => clearTimeout(this._timer));
    this._load().catch(reportError);
  }

  public buildDom() {
    return [
      dom.maybe(use => showSetupBanner(use(this._view)) ? use(this._view) : null, view => cssBanner(
        cssTitle(
          bannerTitle(view),
          cssCount(t("{{done}} of {{total}} ready", { done: finishedSteps(view), total: totalSteps(view) })),
        ),
        view.state === "setting_up" && view.step && view.step !== "done" ?
          cssLine(t("Now: {{step}}", { step: setupStepLabel(view.step) }), testId("current")) : null,
        cssSteps(setupStepRows(view).map(s => cssStep(
          cssStep.cls("-failed", s.state === "failed"),
          cssStep.cls("-pending", s.state === "pending"),
          cssMark(MARKS[s.state] ?? "·"),
          dom("span", setupStepLabel(s.step)),
          s.state === "skipped" ? cssReason(s.reason || t("not in plan")) : null,
          s.state === "failed" && s.reason ? cssReason(s.reason) : null,
          s.state === "pending" ? cssReason(t("not yet")) : null,
          testId("step"),
        ))),
        view.canRetry ? textButton(
          t("Retry"),
          dom.boolAttr("disabled", this._retrying),
          dom.on("click", () => this._retry().catch(reportError)),
          testId("retry"),
        ) : null,
        testId("banner"),
      )),
      dom.maybe(use => use(this._view)?.state === "ready" && use(this._view)?.canRecheck, () => cssRecheck(
        t("All services are set up."), " ",
        textButton(
          t("Re-check setup"),
          dom.boolAttr("disabled", this._retrying),
          dom.on("click", () => this._retry().catch(reportError)),
          testId("recheck"),
        ),
      )),
    ];
  }

  /** Reads the status; keeps polling while it is setting up. Any error hides the banner. */
  private async _load() {
    const resp = await fetchFromHome(`/api/knet/setup/${this._wsId}`, { credentials: "include" });
    if (this.isDisposed()) { return; }
    const view: WorkspaceSetupView | null = resp.ok ? await resp.json() : null;
    if (this.isDisposed()) { return; }
    this._view.set(view);
    if (view?.state === "setting_up") {
      this._timer = setTimeout(() => this._load().catch(reportError), POLL_MS);
    }
  }

  private async _retry() {
    this._retrying.set(true);
    try {
      const resp = await fetchFromHome(`/api/knet/setup/${this._wsId}/retry`, {
        method: "POST",
        credentials: "include",
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });
      if (!resp.ok) { throw new Error(t("Setup could not be started; try again later")); }
      clearTimeout(this._timer);
      await this._load();
    }
    finally {
      if (!this.isDisposed()) { this._retrying.set(false); }
    }
  }
}

function bannerTitle(view: WorkspaceSetupView): string {
  switch (view.state) {
    case "failed": return t("Some services couldn't be set up");
    case "setting_up": return t("Setting up this workspace's services");
    case "stale": return t("This workspace's setup needs to run again");
    default: return t("Setup hasn't run for this workspace yet");
  }
}

interface OrgSetupItem { ws: number; name: string; state: string }

/** The org home page's list of workspaces that need setup; shown to org owners only. */
export class KnetOrgSetupNotice extends Disposable {
  private _items = Observable.create<OrgSetupItem[]>(this, []);

  constructor(private _orgId: number) {
    super();
    this._load().catch(reportError);
  }

  public buildDom() {
    return dom.maybe(use => use(this._items).length ? use(this._items) : null, items => cssBanner(
      cssTitle(t("Some workspaces need setup")),
      cssSteps(items.map(item => cssStep(
        cssStep.cls("-failed", item.state === "failed"),
        cssMark(item.state === "failed" ? "✗" : "·"),
        dom("a", item.name, urlState().setLinkUrl({ ws: item.ws }), testId("org-ws")),
        cssReason(item.state === "failed" ? t("failed") :
          item.state === "stale" ? t("needs to run again") : t("not set up yet")),
      ))),
      testId("org-notice"),
    ));
  }

  private async _load() {
    const resp = await fetchFromHome(`/api/knet/setup/org/${this._orgId}`, { credentials: "include" });
    if (this.isDisposed() || !resp.ok) { return; }
    const body = await resp.json();
    if (this.isDisposed()) { return; }
    this._items.set(Array.isArray(body?.workspaces) ? body.workspaces : []);
  }
}

const cssBanner = styled("div", `
  color: ${theme.text};
  border: 1px solid ${theme.pagePanelsBorder};
  border-radius: 4px;
  padding: 12px 16px;
  margin: 16px 0 8px 0;
  max-width: 550px;
`);

const cssTitle = styled("div", `
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-weight: 600;
`);

const cssCount = styled("span", `
  color: ${theme.lightText};
  font-weight: normal;
`);

const cssLine = styled("div", `
  color: ${theme.lightText};
  margin-top: 4px;
`);

const cssSteps = styled("div", `
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  margin: 8px 0;
`);

const cssStep = styled("div", `
  display: flex;
  gap: 4px;
  &-failed {
    color: ${theme.errorText};
  }
  &-pending {
    color: ${theme.lightText};
  }
`);

const cssRecheck = styled("div", `
  color: ${theme.lightText};
  margin: 12px 0 4px 0;
`);

const cssMark = styled("span", `
  width: 1em;
  text-align: center;
`);

const cssReason = styled("span", `
  color: ${theme.lightText};
`);
